import { type Disclosure } from '@/components/common/helpers';
import { UserMultiSelect } from '@/components/common/input/users';
import { BasicModal, BasicModalFooter } from '@/components/common/modal';
import { Debug } from '@/components/common/output/debug';
import { useApiWithAuth } from '@/hooks/api';
import type { Ministry } from '@/shared/types/ministry';
import { useOrgStore } from '@/store/org';
import { createMinistry, updateMinistry } from '@/store/org/actions';
import {
  AllMinistryRoleKeys,
  getMinistryTypeIcon,
  getMinistryTypeName,
  getOrgAlias,
  getOrgRoleLabel,
  type MinistryRoleKey,
} from '@/store/org/types';
import { ModalBody, ModalHeader, Switch } from '@heroui/react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useEffect, useState } from 'react';

export function MinistryModal({
  disclosure,
  item,
}: {
  disclosure: Disclosure;
  item: Ministry;
}) {
  const store = useOrgStore();
  const { api, auth } = useApiWithAuth();
  const typeLabel = getOrgAlias('ministry', 'type');
  const ministryName = getMinistryTypeName(item.data.type);
  const ministryIcon = getMinistryTypeIcon(item.data.type);
  const ready = Boolean(item);
  const [saving, setSaving] = useState(false);
  const [editable, setEditable] = useState(
    item || ({ id: '', data: {} } as Ministry),
  );
  useEffect(() => {
    if (!disclosure.isOpen) {
      return;
    }

    const value = item || ({ id: '', data: {} } as Ministry);
    console.debug('setting editable:', JSON.stringify(value));
    setEditable(value);
  }, [item, disclosure.isOpen]);
  const updateEditable = (data: Partial<Ministry['data']>) => {
    const updated: Ministry = {
      id: editable.id || item?.id || '',
      data: {
        ...item?.data,
        ...editable.data,
        ...data,
      },
    };
    setEditable(updated);
  };

  const onActiveChange = (value: boolean) => {
    updateEditable({
      active: value,
    });
  };

  const onRoleChange = (role: MinistryRoleKey, ids: string[]) => {
    updateEditable({
      roles: {
        ...(editable.data.roles || {}),
        [role]: ids,
      },
    });
  };

  const onSave = async () => {
    setSaving(true);
    if (editable.id) {
      await updateMinistry(store, { api, auth, item: editable });
    } else {
      await createMinistry(store, { api, auth, item: editable });
    }

    setSaving(false);
    disclosure.onClose();
  };

  return (
    <BasicModal disclosure={disclosure} ready={ready}>
      <ModalHeader>Edit {typeLabel}</ModalHeader>
      <ModalBody>
        <div className='mb-2 flex flex-row items-center gap-1'>
          <Icon icon={ministryIcon} width={20} />
          <span>{ministryName}</span>
        </div>
        <Switch
          isSelected={editable.data.active}
          onValueChange={onActiveChange}
          size='sm'
        >
          Active
        </Switch>
        {AllMinistryRoleKeys.map((role) => (
          <UserMultiSelect
            key={role}
            label={getOrgRoleLabel('ministry', role, true)}
            roles={['people', 'admin']}
            status='unarchived'
            initialSelected={item.data.roles?.[role] || []}
            onChange={(ids: string[]) => onRoleChange(role, ids)}
          />
        ))}
        <Debug value={editable} />
      </ModalBody>
      <BasicModalFooter
        applyLabel='Save'
        applyLoading={saving}
        cancelDisabled={saving}
        onApply={onSave}
        onCancel={disclosure.onClose}
      />
    </BasicModal>
  );
}
